import { ReactElement } from 'react';
import { IActivityWithAvailability } from '@/common/domain/Merge.domain';
import { ImageLabels } from './ImageLabels';
import { NewImagesGalleryComponent } from '@/shared/ImagesGallery/NewImagesGallery';
import * as S from './NewImagesGallery.styles';
import URL from '@/common/app/utils/urlsUtils';
import { TNewAssets } from '@/screens/ActivityPage/service/createAssets';
import { myImageLoader } from '@/lib/imageLoader';
import { defaultPlaceholder } from '@/shared/images/preloader/defaultPlaceholder';

type ImagesGalleryProps = {
    activity?: IActivityWithAvailability;
    imageUrls?: string[];
    reviewsCount?: number;
    disabled?: boolean;
    position: number;
    item_list_id: string;
    item_list_name: string;
    withPaddings?: boolean;
    event: boolean;
    isBlur?: boolean;
    showLabels?: boolean;
    additionalElement?: JSX.Element;
};

type ExternalAsset = {
    type: 'external';
    id: string;
    placeholder: string;
    url: string;
};

export const NewImagesGallery = ({
    activity,
    imageUrls,
    disabled,
    reviewsCount,
    position,
    withPaddings,
    event,
    item_list_name,
    item_list_id,
    isBlur,
    showLabels = true,
    additionalElement,
}: ImagesGalleryProps): ReactElement => {
    const activityImages: TNewAssets[] = activity
        ? [
              { type: 'activity', id: activity.id, placeholder: activity.placeholder || '' },
              ...((activity.images?.map(({ id, placeholder }) => ({
                  type: 'photo',
                  id,
                  placeholder: placeholder || '',
              })) || []) as TNewAssets[]),
          ]
        : [];
    const imagesSrc: (TNewAssets | ExternalAsset)[] = imageUrls?.length
        ? imageUrls.map((url, index) => ({
              type: 'external',
              id: `${index}-${url}`,
              placeholder: '',
              url,
          }))
        : activityImages;

    const images =
        imagesSrc.length > 1
            ? [imagesSrc[imagesSrc.length - 1], ...imagesSrc, imagesSrc[0]]
            : imagesSrc;

    const imagesElement = () =>
        images.map((image, index) => {
            const { type, id, placeholder } = image;
            return (
                <S.Container key={index}>
                    {'url' in image ? (
                        <img
                            className="h-full w-full object-cover"
                            src={image.url}
                            alt={`image-${id}`}
                        />
                    ) : (
                        <S.ImageTag
                            loader={myImageLoader}
                            alt={`image-${id}`}
                            src={`${URL.image}/${type}/${id}/center/1040x640/ts`}
                            width={520}
                            height={320}
                            blurDataURL={placeholder || defaultPlaceholder}
                            placeholder="blur"
                            objectFit="cover"
                            loading="lazy"
                        />
                    )}
                </S.Container>
            );
        });

    return (
        <NewImagesGalleryComponent
            additionalElement={
                <>
                    {showLabels && activity && (
                        <ImageLabels
                            activity={activity}
                            reviewsCount={reviewsCount}
                            disabled={disabled}
                            position={position}
                            item_list_name={item_list_name}
                            item_list_id={item_list_id}
                        />
                    )}
                    {additionalElement}
                </>
            }
            items={images}
            itemsElement={imagesElement}
            withPaddings={withPaddings}
            event={event}
            isBlur={isBlur}
        />
    );
};
